import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { FaArrowLeft, FaStar, FaUserCircle } from 'react-icons/fa';
import { FiChevronRight } from 'react-icons/fi';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { canBandTakeTips, getCurrentUserId, getDonationMethods } from '../_helpers/bandUtils.js';
import { SongStatus } from '../_helpers/songStatus.js';
import { isSignedIn, rememberReturnPath } from '../_helpers/safeRedirect.js';
import { resolveMediaUrl } from '../_helpers/mediaUrl.js';
import config from '../config.js';
import { SWITCH_ERRORS } from '../_helpers/switchErrors.js';
import TipModal from './partial/modal/TipModal.jsx';
import VinylSleeve, { coverLabel } from './partial/VinylSleeve.jsx';
import FavoriteButton from './partial/FavoriteButton.jsx';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import { useFavorites } from '../hooks/useFavorites.js';
import { useT } from '../contexts/LocaleContext.jsx';
import '../styles/PublicSongList.css';

// The backend's largest page (songs.go maxPerPage); a bigger per_page is a 400.
const SONGS_PER_PAGE = 100;

// The whole active songlist, every page of it (ENG-660): the endpoint pages
// 25 at a time by default, and this page searches, filters and sorts on the
// client, so it used to show only the first 25 songs. A first page that fails
// is an empty list, like the other sub-lists; a later page that fails rejects,
// so the page shows its load error instead of a list cut off part way.
// With sortBy 'magic' the server orders the list (ENG-162: most-favorited
// first) and the page keeps that order rather than sorting it (ENG-264).
function fetchAllPublicSongs(fetchWrapper, bandid, sortBy = null) {
  const sortParam = sortBy ? `&sort_by=${sortBy}` : '';
  const songsUrl = (page) =>
    `${config.apiBaseUrl}/public/bands/${bandid}/songs?page=${page}&per_page=${SONGS_PER_PAGE}${sortParam}`;
  // The first request goes out synchronously, like its siblings in fetchData.
  return fetchWrapper.get(songsUrl(1)).catch(() => []).then((first) => fetchRemainingPages(first, fetchWrapper, songsUrl));
}

async function fetchRemainingPages(first, fetchWrapper, songsUrl) {
  // Fallback for the old API format, a bare array with no paging
  if (Array.isArray(first)) return first;
  if (!first?.success || !Array.isArray(first.songs)) {
    console.warn('Unexpected songs response format:', first);
    return [];
  }
  const total = Number(first.total) || 0;
  const pageCount = Math.ceil(total / SONGS_PER_PAGE);
  if (first.songs.length === 0 || pageCount <= 1) return first.songs;

  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) => fetchWrapper.get(songsUrl(i + 2)))
  );
  // A song added or removed mid-load shifts the pages; keep each song once.
  const seen = new Set();
  return [first, ...rest]
    .flatMap((response) => (Array.isArray(response?.songs) ? response.songs : []))
    .filter((song) => {
      const key = song?.id ?? song?.ID;
      if (key == null) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}


const PublicSongList = () => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fetchWrapper = useFetchWrapper();
  const t = useT();
  // Switch mode (ENG-683): opened from the stream page's YOUR SONG card with
  // ?switch=<entryId>, the chosen song replaces the one on that request.
  const switchEntryId = Number(new URLSearchParams(location.search).get('switch')) || null;
  const [switchError, setSwitchError] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [songs, setSongs] = useState([]);
  const [bandTags, setBandTags] = useState([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  // Catalog key of the load error, translated at render so a locale switch
  // applies without re-running the fetch.
  const [error, setError] = useState(null);
  const [bandInfo, setBandInfo] = useState(null);
  const [isBandLive, setIsBandLive] = useState(false);
  // The live show's re-request setting, read with isLive from
  // `/public/bands/{id}/stream/status` when the page loads; null when the
  // status carried none (or the band was not live)
  const [liveShowAllowRepeatRequests, setLiveShowAllowRepeatRequests] = useState(null);
  const [bandQueue, setBandQueue] = useState([]);

  const donationMethods = useMemo(() => getDonationMethods(bandInfo, t), [bandInfo, t]);

  const hasDonationMethods = canBandTakeTips(bandInfo);
  const [userBands, setUserBands] = useState([]);
  // Magic is the server's order and is offered only to a signed-in listener
  // (ENG-264); signed out, the list starts A-Z by song name instead.
  const signedIn = isSignedIn();
  const [sortMode, setSortMode] = useState(signedIn ? 'magic' : 'songName');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [requestModal, setRequestModal] = useState(null); // { song, artworkUrl }
  const [tipModalOpen, setTipModalOpen] = useState(false);
  const [tipInitialAmount, setTipInitialAmount] = useState(null);
  const [requestName, setRequestName] = useState('');
  const [requestStep, setRequestStep] = useState('form'); // 'form' | 'confirm'
  const [submittedEntry, setSubmittedEntry] = useState(null);
  // A failed request: the backend's words, or null for the generic message.
  const [requestError, setRequestError] = useState(null);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [displayedSongs, setDisplayedSongs] = useState([]);
  const [songListAnimationState, setSongListAnimationState] = useState('idle');
  const songListAnimationTimeoutRef = useRef({ exit: null, enter: null });
  const hasAnimatedInitialLoadRef = useRef(false);

  // The listener's favorites (ENG-443). A toggle changes only the star, never
  // `songs`, so the list keeps its order until a reload or Magic is chosen
  // again. The filter shows the favorites as they were when it was turned on,
  // so unstarring a song there leaves it in place instead of pulling it out
  // from under the pointer.
  const favorites = useFavorites(bandid);
  const { toggle: toggleFavorite } = favorites;
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoritesShown, setFavoritesShown] = useState(() => new Set());
  const [favoriteSignIn, setFavoriteSignIn] = useState(false);

  const handleFavorite = useCallback(async (song) => {
    const result = await toggleFavorite(song.id);
    if (result === 'signin') setFavoriteSignIn(true);
  }, [toggleFavorite]);

  const toggleFavoritesOnly = useCallback(() => {
    if (!favoritesOnly) setFavoritesShown(new Set(favorites.ids));
    setFavoritesOnly(!favoritesOnly);
  }, [favoritesOnly, favorites.ids]);

  const openRequestModal = useCallback((song, artworkUrl) => {
    setRequestModal({ song, artworkUrl });
    setRequestName('');
    // POST /queue needs a session: a signed-out visitor gets a prompt to
    // sign in instead of a form that could only fail (ENG-503).
    setRequestStep(isSignedIn() ? 'form' : 'signin');
    setSubmittedEntry(null);
    setRequestError(undefined);
  }, []);

  const closeRequestModal = useCallback(() => {
    setRequestModal(null);
  }, []);

  const backToStream = useCallback(() => navigate(`/performers/${bandid}/stream`), [navigate, bandid]);

  // Puts song on the listener's request (POST /bands/{id}/queue/entries/{entryId}/switch,
  // ENG-684) and goes back to the stream, where the card shows the new song.
  const switchTo = useCallback(async (song) => {
    if (!switchEntryId || switching) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      await fetchWrapper.post(`${config.apiBaseUrl}/bands/${bandid}/queue/entries/${switchEntryId}/switch`, { song_id: song.id });
      backToStream();
    } catch (err) {
      setSwitching(false);
      setSwitchError(SWITCH_ERRORS[err?.code] ?? 'publicSongList.switch.errors.generic');
    }
  }, [switchEntryId, switching, fetchWrapper, bandid, backToStream]);

  const openTipModal = useCallback((amount = null) => {
    setTipInitialAmount(amount);
    setTipModalOpen(true);
  }, []);

  const closeTipModal = useCallback(() => {
    setTipModalOpen(false);
    setTipInitialAmount(null);
  }, []);

  const handleSortClick = useCallback((newMode) => {
    // If clicking the same mode, toggle direction; otherwise switch mode and reset to asc
    if (sortMode === newMode) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortMode(newMode);
      setSortDirection('asc');
    }
  }, [sortMode, sortDirection]);

  const handleRequestSubmit = useCallback(async () => {
    if (!requestModal) return;
    // A request is a title, an artist and an optional name. The $20/$50/$100
    // tiers and Boost that used to be here sent a donation_amount and a
    // position with no payment behind them (ENG-495); paid priority comes back
    // only through the paid boost endpoint (ENG-444/ENG-445), and the backend
    // ignores a client-sent amount (ENG-487).
    const payload = {
      band_id: Number(bandid),
      title: requestModal.song.title,
      artist: requestModal.song.artist,
      requested_by: requestName.trim() || '',
    };
    setRequestSubmitting(true);
    setRequestError(undefined);
    try {
      const response = await fetchWrapper.post(`${config.apiBaseUrl}/queue`, payload);
      const entryId = response?.ID ?? response?.id ?? null;

      const queueResponse = await fetchWrapper
        .get(`${config.apiBaseUrl}/public/bands/${bandid}/queue`)
        .catch(() => []);
      const updatedQueue = Array.isArray(queueResponse) ? queueResponse : [];
      setBandQueue(updatedQueue);

      let position = null;
      if (entryId !== null) {
        const idx = updatedQueue.findIndex((e) => String(e.ID ?? e.id) === String(entryId));
        if (idx !== -1) position = idx + 1;
      }
      if (position === null) {
        for (let i = updatedQueue.length - 1; i >= 0; i--) {
          if (
            updatedQueue[i].song?.Title === requestModal.song.title &&
            updatedQueue[i].song?.Artist === requestModal.song.artist
          ) {
            position = i + 1;
            break;
          }
        }
      }

      setSubmittedEntry({
        id: entryId,
        position,
        song: requestModal.song,
        requestedBy: requestName.trim() || '',
        artworkUrl: requestModal.artworkUrl,
      });
      setRequestStep('confirm');
    } catch (err) {
      console.warn('Song request failed:', err);
      // Keep the dialog open and say so. The server's wording is shown when
      // it sent some (fetchWrapper falls back to the status text otherwise).
      const message = typeof err?.message === 'string' ? err.message.trim() : '';
      setRequestError(message && err?.status && message !== err.statusText ? message : null);
    } finally {
      setRequestSubmitting(false);
    }
  }, [requestModal, requestName, bandid, fetchWrapper]);

  const previewAudioRef = useRef(null);
  const activePreviewSongIdRef = useRef(null);

  const resolveMediaUrl = useCallback((url) => {
    if (!url) return null;
    const base = (config.mediaBaseUrl || '').replace(/\/$/, '');
    const path = url.replace(/^\//, '');
    return base ? `${base}/${path}` : path;
  }, []);

  // Check if user is logged in
  const userId = getCurrentUserId();
  
  // Check if user is a member of the band (check if current band is in user's bands list)
  const isUserBandMember = useMemo(() => {
    if (!userId || !userBands.length) return false;
    return userBands.some(band => (band.ID ?? band.id)?.toString() === bandid?.toString());
  }, [userBands, bandid, userId]);

  // The band takes free requests unless it says it doesn't (ENG-664/665); its
  // members queue songs whatever the setting.
  const freeRequestsOpen = bandInfo?.freeRequestsEnabled !== false || isUserBandMember;

  // Only the latest load may set state: an earlier one that answers later
  // (a Retry clicked while it was still out) would otherwise put its error
  // back over the list the Retry loaded (ENG-533).
  const loadSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;
    // Without a band ID there is nothing to ask for (ENG-514): it used to
    // request /public/bands/undefined/... and show "No songs available".
    if (!/^[1-9]\d*$/.test(String(bandid ?? ''))) {
      setError('publicSongList.state.notFound');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const fetchPromises = [
        fetchAllPublicSongs(fetchWrapper, bandid, signedIn ? 'magic' : null),
        fetchWrapper.get(`${config.apiBaseUrl}/public/bands/${bandid}/tags`).catch(() => []),
        // The band itself decides whether there is a page at all; only the
        // sub-lists fall back to empty (ENG-514).
        fetchWrapper.get(`${config.apiBaseUrl}/public/bands/${bandid}`).then(
          (data) => ({ data }),
          (failure) => ({ failure })
        ),
        fetchWrapper.get(`${config.apiBaseUrl}/public/bands/${bandid}/stream/status`).catch(() => null),
      ];

      if (userId) {
        fetchPromises.push(
          fetchWrapper.get(`${config.apiBaseUrl}/user/mybands`).catch(() => [])
        );
      }

      const results = await Promise.all(fetchPromises);
      if (isStale()) return;
      const [allSongs, tagsResponse, bandResult, streamStatusResponse, userBandsData] = results;
      if (bandResult.failure) {
        console.error('Error fetching band:', bandResult.failure);
        setError(bandResult.failure?.status === 404 ? 'publicSongList.state.notFound' : 'publicSongList.state.loadError');
        return;
      }
      const bandData = bandResult.data;

      setSongs(allSongs);

      // Handle tags response (may be old or new format)
      if (tagsResponse && tagsResponse.success && Array.isArray(tagsResponse.tags)) {
        setBandTags(tagsResponse.tags);
      } else if (Array.isArray(tagsResponse)) {
        setBandTags(tagsResponse);
      } else {
        setBandTags([]);
      }
      
      setBandInfo(bandData);

      const live = Boolean(streamStatusResponse?.isLive);
      setIsBandLive(live);
      setLiveShowAllowRepeatRequests(
        streamStatusResponse?.allowRepeatRequests ?? streamStatusResponse?.allow_repeat_requests ?? null
      );

      if (live) {
        const queueResponse = await fetchWrapper
          .get(`${config.apiBaseUrl}/public/bands/${bandid}/queue`)
          .catch(() => []);
        if (isStale()) return;
        setBandQueue(Array.isArray(queueResponse) ? queueResponse : []);
      } else {
        setBandQueue([]);
      }
      
      // Set user bands only if user is logged in
      if (userId && userBandsData) {
        setUserBands(userBandsData);
      }
    } catch (error) {
      if (isStale()) return;
      console.error('Error fetching songlist:', error);
      setError('publicSongList.state.loadError');
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [bandid, fetchWrapper, userId, signedIn]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Choosing Magic, or choosing it again, reads the server's order afresh, so
  // favorites toggled since the page loaded count (ENG-264). Until it answers
  // the list stays as it is; a failed read keeps it.
  const magicSeqRef = useRef(0);
  const selectMagic = useCallback(() => {
    setSortMode('magic');
    const seq = ++magicSeqRef.current;
    fetchAllPublicSongs(fetchWrapper, bandid, 'magic')
      .then((fresh) => {
        if (seq !== magicSeqRef.current || !Array.isArray(fresh) || fresh.length === 0) return;
        setSongs(fresh);
      })
      .catch(() => {});
  }, [fetchWrapper, bandid]);

  useEffect(() => {
    if (!isBandLive) return;
    let inFlight = false;
    const intervalId = setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const queueResponse = await fetchWrapper
          .get(`${config.apiBaseUrl}/public/bands/${bandid}/queue`)
          .catch(() => null);
        if (queueResponse !== null) {
          const updatedQueue = Array.isArray(queueResponse) ? queueResponse : [];
          setBandQueue(updatedQueue);
          setSubmittedEntry((prev) => {
            if (!prev?.id) return prev;
            const idx = updatedQueue.findIndex((e) => String(e.ID ?? e.id) === String(prev.id));
            return idx !== -1 ? { ...prev, position: idx + 1 } : prev;
          });
        }
      } finally {
        inFlight = false;
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [isBandLive, bandid, fetchWrapper]);

  const getTagId = useCallback((tag) => {
    const rawId = tag?.id ?? tag?.ID ?? null;
    return rawId === null || rawId === undefined ? null : String(rawId);
  }, []);

  const getRequestCount = useCallback((song) => {
    const rawCount =
      song?.request_count ??
      song?.requestCount ??
      song?.requested_count ??
      song?.requestedCount ??
      song?.play_count ??
      song?.playCount ??
      0;

    const parsed = Number(rawCount);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const getSongTimestamp = useCallback((song) => {
    const rawDate = song?.created_at ?? song?.createdAt ?? song?.updated_at ?? song?.updatedAt ?? null;
    if (!rawDate) {
      return 0;
    }

    const timestamp = new Date(rawDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }, []);

  const getReleaseYear = useCallback((song) => {
    if (!song?.release_date) {
      return Number.NEGATIVE_INFINITY;
    }

    const year = new Date(song.release_date).getFullYear();
    return Number.isNaN(year) ? Number.NEGATIVE_INFINITY : year;
  }, []);

  const sortedSongs = useMemo(() => {
    // Normalize string for sorting: strip leading articles and trim whitespace
    const normalizeSortString = (str) => {
      const trimmed = (str ?? '').trim();
      // Remove leading articles (The, A, An) case-insensitively
      return trimmed.replace(/^(the|a|an)\s+/i, '');
    };

    let sortableSongs = [...songs];
    
    // Apply search filter if query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      sortableSongs = sortableSongs.filter(song => {
        const title = (song?.title ?? '').toLowerCase();
        const artist = (song?.artist ?? '').toLowerCase();
        return title.includes(query) || artist.includes(query);
      });
    }
    
    if (favoritesOnly) {
      sortableSongs = sortableSongs.filter((song) => favoritesShown.has(Number(song?.id)));
    }

    // Apply tag filter if selected
    if (selectedTagFilter) {
      const selectedTagId = getTagId(selectedTagFilter);
      sortableSongs = sortableSongs.filter(song => {
        const songTags = song.tags || [];
        return songTags && songTags.some(tag => getTagId(tag) === selectedTagId);
      });
    }
    
    switch (sortMode) {
      case 'mostRequested':
        sortableSongs.sort((a, b) => getRequestCount(b) - getRequestCount(a));
        break;
      case 'newest':
        sortableSongs.sort((a, b) => getSongTimestamp(b) - getSongTimestamp(a));
        break;
      case 'songName': {
        sortableSongs.sort((a, b) => {
          const comparison = normalizeSortString(a?.title).localeCompare(normalizeSortString(b?.title), undefined, { caseFirst: 'lower' });
          return sortDirection === 'asc' ? comparison : -comparison;
        });
        break;
      }
      case 'bandName': {
        sortableSongs.sort((a, b) => {
          const comparison = normalizeSortString(a?.artist).localeCompare(normalizeSortString(b?.artist), undefined, { caseFirst: 'lower' });
          return sortDirection === 'asc' ? comparison : -comparison;
        });
        break;
      }
      case 'year': {
        sortableSongs.sort((a, b) => {
          const comparison = getReleaseYear(a) - getReleaseYear(b);
          return sortDirection === 'asc' ? comparison : -comparison;
        });
        break;
      }
      // Magic: the server's order, as loaded (ENG-264)
      case 'magic':
      default:
        break;
    }

    return sortableSongs;
  }, [songs, sortMode, sortDirection, selectedTagFilter, searchQuery, favoritesOnly, favoritesShown, getTagId, getRequestCount, getSongTimestamp, getReleaseYear]);

  useEffect(() => {
    const EXIT_MS = 180;
    const ENTER_MS = 260;

    if (loading) {
      return undefined;
    }

    // Both paths below return this, so no animation timer outlives the list
    // or the effect run that set it (ENG-565). The first-load path used to
    // return nothing, and its timer then set state after unmount: in Vitest,
    // after jsdom was torn down, which failed the run with
    // "window is not defined".
    const clearAnimationTimers = () => {
      clearTimeout(songListAnimationTimeoutRef.current.exit);
      clearTimeout(songListAnimationTimeoutRef.current.enter);
      songListAnimationTimeoutRef.current.exit = null;
      songListAnimationTimeoutRef.current.enter = null;
    };

    clearAnimationTimers();

    if (!hasAnimatedInitialLoadRef.current) {
      setDisplayedSongs(sortedSongs);
      setSongListAnimationState('idle');
      hasAnimatedInitialLoadRef.current = true;

      // Trigger enter animation on next frame so the class change is always painted.
      songListAnimationTimeoutRef.current.enter = setTimeout(() => {
        setSongListAnimationState('in');
        songListAnimationTimeoutRef.current.enter = setTimeout(() => {
          setSongListAnimationState('idle');
        }, ENTER_MS);
      }, 16);
      return clearAnimationTimers;
    }

    setSongListAnimationState('out');
    songListAnimationTimeoutRef.current.exit = setTimeout(() => {
      setDisplayedSongs(sortedSongs);
      setSongListAnimationState('in');
      songListAnimationTimeoutRef.current.enter = setTimeout(() => {
        setSongListAnimationState('idle');
      }, ENTER_MS);
    }, EXIT_MS);

    return clearAnimationTimers;
  }, [sortedSongs, loading]);

  const selectedTagLabel = selectedTagFilter?.Word || selectedTagFilter?.word || t('publicSongList.list.selectedTagFallback');

  const getCoverLabel = (song) => coverLabel(song?.original_album || song?.title, t('publicSongList.card.coverFallback'));

  const getSongYear = (song) => {
    if (!song?.release_date) {
      return song?.original_album || '';
    }

    const year = new Date(song.release_date).getFullYear();
    return Number.isNaN(year) ? song.release_date : year;
  };

  const stopPreview = useCallback(() => {
    const previewAudio = previewAudioRef.current;
    if (!previewAudio) {
      return;
    }

    previewAudio.pause();
    previewAudio.currentTime = 0;
    activePreviewSongIdRef.current = null;
  }, []);

  const playPreview = useCallback((song) => {
    const previewUrl = song?.preview_url;
    if (!previewUrl) {
      stopPreview();
      return;
    }

    const previewAudio = previewAudioRef.current || new Audio();
    previewAudioRef.current = previewAudio;

    if (activePreviewSongIdRef.current !== song.id) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      previewAudio.src = previewUrl;
      previewAudio.preload = 'auto';
      activePreviewSongIdRef.current = song.id;
    }

    const playPromise = previewAudio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((error) => {
        console.warn('Failed to play song preview:', error);
        stopPreview();
      });
    }
  }, [stopPreview]);

  // The rail lists only what is playing and still to come (ENG-573): played
  // and skipped entries are history, not the queue. GET /public/bands/{id}/queue
  // already answers with only those, in the order the band plays them; the
  // filter keeps it so whatever queue data lands here. The server's order is
  // kept, since the request dialog's "you're #N" counts through the same list.
  const queueSongs = useMemo(() => {
    const statusOf = (entry) => entry?.Status ?? entry?.status;
    return bandQueue
      .filter((entry) => {
        const status = statusOf(entry);
        return status === SongStatus.QUEUED || status === SongStatus.PLAYING;
      })
      .map((entry) => ({
      id: entry?.ID,
      title: entry?.song?.Title,
      artist: entry?.song?.Artist,
      requestedBy: entry?.requested_by,
      donationAmount: entry?.donation_amount || 0,
      artworkUrl: resolveMediaUrl(entry?.song?.artwork_url || null),
    }));
  }, [bandQueue, resolveMediaUrl]);

  const songQueueStatusMap = useMemo(() => {
    const statusMap = new Map();

    bandQueue.forEach((entry) => {
      const songId = entry?.SongID;
      const status = entry?.Status ?? entry?.status;

      if (songId !== null && songId !== undefined) {
        statusMap.set(songId, status);
      }
    });

    return statusMap;
  }, [bandQueue]);

  const getQueueMarker = useCallback((songId) => {
    const status = songQueueStatusMap.get(songId);

    if (status === 0 || status === 1) {
      return t('publicSongList.card.inQueue');
    }

    if (status === 3) {
      return t('publicSongList.card.played');
    }

    return '';
  }, [songQueueStatusMap, t]);

  const isSongInQueue = useCallback((songId) => {
    const status = songQueueStatusMap.get(songId);
    return status === 0 || status === 1;
  }, [songQueueStatusMap]);

  // Whether a song already played tonight may be requested again. The setting
  // belongs to the event (ENG-352: it is set when the show is created, see
  // EditEventModal) and is reported for the live show on the stream status;
  // absent means allowed. The band-level allow_repeat_requests field (ENG-259)
  // is deliberately not consulted any more: nothing edits it now, so honouring
  // it could keep re-requests off for a band with no way to turn them back on.
  const allowRepeatRequests = liveShowAllowRepeatRequests !== false;

  useEffect(() => {
    return () => {
      stopPreview();
      if (previewAudioRef.current) {
        previewAudioRef.current.src = '';
        previewAudioRef.current = null;
      }
    };
  }, [stopPreview]);

  const sortIndicator = sortDirection === 'asc' ? t('publicSongList.sort.ascending') : t('publicSongList.sort.descending');

  // Every render path carries the listener header, as the stream page does (ENG-801).
  if (loading) {
    return (
      <>
        <StreamPageHeader />
        <div className="public-songlist-container">
          <div className="public-songlist-state-message">{t('loading.songlist')}</div>
        </div>
      </>
    );
  }

  if (error) {
    const notFound = error === 'publicSongList.state.notFound';
    return (
      <>
        <StreamPageHeader />
        <div className="public-songlist-container">
          <div className="public-songlist-state-message" role="alert">
            <p>{t(error)}</p>
            {notFound ? (
              <Link to="/performers">{t('publicSongList.state.findPerformers')}</Link>
            ) : (
              <button type="button" onClick={fetchData}>{t('publicSongList.state.retry')}</button>
            )}
          </div>
        </div>
      </>
    );
  }

  const modalEl = requestModal ? createPortal(
    <div
      className="srm-backdrop"
      onClick={requestStep === 'form' || requestStep === 'signin' ? closeRequestModal : undefined}
      aria-modal="true"
      role="dialog"
    >
      <div className="srm-panel" onClick={(e) => e.stopPropagation()}>
        {requestStep === 'form' && (
          <>
            <div className="srm-song-row">
              {requestModal.artworkUrl ? (
                <img src={requestModal.artworkUrl} alt="" className="srm-artwork" />
              ) : (
                <div className="srm-artwork srm-artwork--fallback">
                  {requestModal.song.title?.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="srm-song-info">
                <div className="srm-song-title">{requestModal.song.title}</div>
                <div className="srm-song-artist">{requestModal.song.artist}</div>
              </div>
            </div>

            <label className="srm-label" htmlFor="srm-name">{t('publicSongList.request.nameLabel')}</label>
            <input
              id="srm-name"
              data-testid="song-request-name"
              type="text"
              className="srm-input"
              placeholder={t('publicSongList.request.namePlaceholder')}
              value={requestName}
              onChange={(e) => setRequestName(e.target.value)}
            />

            {requestError !== undefined && (
              <div className="srm-error" role="alert" data-testid="song-request-error">
                {requestError || t('publicSongList.request.failed')}
              </div>
            )}

            <div className="srm-actions">
              <button type="button" className="srm-cancel" onClick={closeRequestModal}>{t('common.actions.cancel')}</button>
              <button type="button" className="srm-submit" onClick={handleRequestSubmit} disabled={requestSubmitting} data-testid="song-request-submit">
                {t('publicSongList.request.submit')}
              </button>
            </div>
          </>
        )}

        {requestStep === 'signin' && (
          <>
            <p className="srm-signin-text">{t('publicSongList.request.signInPrompt')}</p>
            <div className="srm-actions">
              <button type="button" className="srm-cancel" onClick={closeRequestModal}>{t('common.actions.cancel')}</button>
              <button
                type="button"
                className="srm-submit"
                onClick={() => {
                  rememberReturnPath();
                  navigate('/login');
                }}
              >
                {t('common.actions.signIn')}
              </button>
            </div>
          </>
        )}

        {requestStep === 'confirm' && (
          <>
            <div className="srm-confirm-header">
              <div className="srm-confirm-icon" aria-hidden="true">✓</div>
              <div className="srm-confirm-title">{t('publicSongList.request.submitted')}</div>
            </div>

            <div className="srm-song-row">
              {submittedEntry?.artworkUrl ? (
                <img src={submittedEntry.artworkUrl} alt="" className="srm-artwork" />
              ) : (
                <div className="srm-artwork srm-artwork--fallback">
                  {submittedEntry?.song.title?.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="srm-song-info">
                <div className="srm-song-title">{submittedEntry?.song.title}</div>
                <div className="srm-song-artist">{submittedEntry?.song.artist}</div>
              </div>
            </div>

            {isBandLive && submittedEntry?.position !== null && submittedEntry?.position !== undefined && (
              <div className="srm-queue-position" aria-live="polite">
                {t('publicSongList.request.queuePosition', { position: submittedEntry.position })}
              </div>
            )}

            <div className="srm-actions">
              <button
                type="button"
                className="srm-cancel srm-cancel--ml-auto"
                onClick={closeRequestModal}
              >
                {t('common.actions.done')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  const favoriteSignInEl = favoriteSignIn ? createPortal(
    <div className="srm-backdrop" onClick={() => setFavoriteSignIn(false)} aria-modal="true" role="dialog" data-testid="favorite-signin">
      <div className="srm-panel" onClick={(e) => e.stopPropagation()}>
        <p className="srm-signin-text">{t('favorites.signInPrompt')}</p>
        <div className="srm-actions">
          <button type="button" className="srm-cancel" onClick={() => setFavoriteSignIn(false)}>{t('common.actions.cancel')}</button>
          <button
            type="button"
            className="srm-submit"
            onClick={() => {
              rememberReturnPath();
              navigate('/login');
            }}
          >
            {t('common.actions.signIn')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
    <StreamPageHeader />
    <div className="public-songlist-container">
      {modalEl}
      {favoriteSignInEl}
      {switchEntryId && (
        <div className="public-songlist-switch-banner" role="status" data-testid="switch-banner">
          <span>{switchError ? t(switchError) : t('publicSongList.switch.pick')}</span>
          <button type="button" onClick={backToStream}>{t('publicSongList.switch.cancel')}</button>
        </div>
      )}
      <TipModal
        isOpen={tipModalOpen}
        onClose={closeTipModal}
        bandInfo={bandInfo}
        bandId={bandid}
        fetchWrapper={fetchWrapper}
        apiBaseUrl={config.apiBaseUrl}
        initialAmount={tipInitialAmount}
      />
      <div className={`public-songlist-shell public-songlist-shell--with-rail${requestModal ? ' modal-open' : ''}`}>
        <section className="public-songlist-hero">
          <div className="public-songlist-hero-content">
            <div className="public-songlist-band-logo">
                {bandInfo?.logoUrl ? (
                  <img
                    src={bandInfo.logoUrl}
                    alt={t('publicSongList.hero.logoAlt', { name: bandInfo.name || t('publicSongList.hero.bandFallback') })}
                    className="public-songlist-logo-image"
                  />
                ) : (
                  <FaUserCircle className="public-songlist-logo-image" />
                )}
              </div>
              <div className="public-songlist-header-text">
                <p className="public-songlist-eyebrow">{t('publicSongList.hero.eyebrow')}</p>
                <h1>{t('publicSongList.hero.title', { name: bandInfo?.name || t('publicSongList.hero.bandFallback') })}</h1>
              </div>
            </div>
        </section>

        <div className="public-songlist-body">
          <section className="public-songlist-main public-songlist-main--with-rail">
          {userId && (
            <button
              type="button"
              className="back-arrow"
              onClick={() => {
                if (isUserBandMember) {
                  navigate(`/performers/${bandid}/song-list`);
                } else {
                  navigate(`/performers/${bandid}`);
                }
              }}
            >
              <FaArrowLeft className="rtl-mirror" /> {isUserBandMember ? t('publicSongList.back.toBandSonglist') : t('publicSongList.back.toBandPage')}
            </button>
          )}

          {!freeRequestsOpen && !switchEntryId && (
            <p className="public-songlist-free-off" role="status" data-testid="free-requests-off">
              {t('publicSongList.freeRequestsOff')}
            </p>
          )}

          {bandInfo?.songlistMessage && (
            <div className="public-songlist-description-container">
              <p className="public-songlist-description">{bandInfo.songlistMessage}</p>
            </div>
          )}

          <div className="public-songlist-filter-panel">
            <div className="public-songlist-search-container">
              <input
                type="text"
                className="public-songlist-search-input"
                data-testid="public-songlist-search"
                placeholder={t('publicSongList.filters.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {signedIn && (
              <div className="public-songlist-favorites-filter">
                <button
                  type="button"
                  className={`public-songlist-chip${favoritesOnly ? ' active' : ''}`}
                  aria-pressed={favoritesOnly}
                  disabled={favorites.status !== 'ready'}
                  onClick={toggleFavoritesOnly}
                  data-testid="favorites-filter"
                >
                  <FaStar aria-hidden="true" /> {t('publicSongList.filters.favorites')}
                </button>
                {favorites.status === 'error' && (
                  <span className="public-songlist-favorites-note" role="status">{t('favorites.loadFailed')}</span>
                )}
              </div>
            )}

            {bandTags.length > 0 && (
              <div className="public-songlist-tags-group">
                <div className="public-songlist-tags-header">
                  <span className="public-songlist-ctrl-label">{t('publicSongList.filters.tags')}</span>
                  <button
                    type="button"
                    className={`public-songlist-tags-toggle${tagsExpanded ? ' expanded' : ''}`}
                    onClick={() => setTagsExpanded(!tagsExpanded)}
                    aria-label={t('publicSongList.filters.toggleTags')}
                  >
                    <FiChevronRight aria-hidden="true" />
                  </button>
                </div>
                {tagsExpanded && (
                  <div className="public-songlist-tags-list">
                    {bandTags.map((tag) => {
                      const tagId = tag.ID || tag.id;
                      const tagWord = tag.Word || tag.word;
                      const tagBg = tag.BackgroundColor || tag.background_color;
                      const tagText = tag.TextColor || tag.text_color;
                      const tagImage = tag.ImageURL || tag.image_url;
                      const isActive = selectedTagFilter &&
                        (selectedTagFilter.ID || selectedTagFilter.id) === tagId;
                      return (
                        <button
                          key={tagId}
                          type="button"
                          className={`public-songlist-tag-chip${isActive ? ' active' : ''}`}
                          style={{ '--tag-bg': tagBg, '--tag-text': tagText }}
                          onClick={() => setSelectedTagFilter(isActive ? null : tag)}
                        >
                          {tagImage && (
                            <img
                              src={resolveMediaUrl(tagImage)}
                              alt=""
                              className="public-songlist-tag-chip-icon"
                            />
                          )}
                          {tagWord}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="public-songlist-controls" aria-label={t('publicSongList.sort.groupLabel')}>
            <div className="public-songlist-ctrl-group">
              <span className="public-songlist-ctrl-label">{t('publicSongList.sort.label')}</span>
              {signedIn && (
                <button
                  type="button"
                  className={`public-songlist-chip ${sortMode === 'magic' ? 'active' : ''}`}
                  onClick={selectMagic}
                  data-testid="sort-magic"
                >
                  {t('publicSongList.sort.magic')}
                </button>
              )}
              <button
                type="button"
                className={`public-songlist-chip ${sortMode === 'mostRequested' ? 'active' : ''}`}
                onClick={() => setSortMode('mostRequested')}
              >
                {t('publicSongList.sort.mostRequested')}
              </button>
              <button
                type="button"
                className={`public-songlist-chip ${sortMode === 'newest' ? 'active' : ''}`}
                onClick={() => setSortMode('newest')}
              >
                {t('publicSongList.sort.newest')}
              </button>
              <button
                type="button"
                className={`public-songlist-chip ${sortMode === 'songName' ? 'active' : ''}`}
                onClick={() => handleSortClick('songName')}
              >
                {t('publicSongList.sort.songName')} {sortMode === 'songName' && sortIndicator}
              </button>
              <button
                type="button"
                className={`public-songlist-chip ${sortMode === 'bandName' ? 'active' : ''}`}
                onClick={() => handleSortClick('bandName')}
              >
                {t('publicSongList.sort.bandName')} {sortMode === 'bandName' && sortIndicator}
              </button>
              <button
                type="button"
                className={`public-songlist-chip ${sortMode === 'year' ? 'active' : ''}`}
                onClick={() => handleSortClick('year')}
              >
                {t('publicSongList.sort.byYear')} {sortMode === 'year' && sortIndicator}
              </button>
            </div>

            <span className="public-songlist-count">
              {t('publicSongList.list.songCount', { shown: sortedSongs.length, count: songs.length })}
            </span>
          </div>

          {favorites.error && (
            <p className="public-songlist-favorite-error" role="alert" data-testid="favorite-error">
              {t('favorites.failed')}
            </p>
          )}

            <div
              className={`public-song-list ${songListAnimationState === 'out' ? 'public-song-list--out' : ''} ${songListAnimationState === 'in' ? 'public-song-list--in' : ''}`}
            >
              {displayedSongs.length === 0 ? (
                <div className="no-songs-message" data-testid="public-songlist-empty">
                  {favoritesOnly && favoritesShown.size === 0 && t('publicSongList.list.noFavorites')}
                  {favoritesOnly && favoritesShown.size > 0 && t('publicSongList.list.noFavoritesMatch')}
                  {!favoritesOnly && (selectedTagFilter
                    ? t('publicSongList.list.noSongsWithTag', { tag: selectedTagLabel })
                    : t('publicSongList.list.noSongs'))}
                </div>
              ) : (
                displayedSongs.map((song, index) => {
                const songId = song.id;
                const songTags = song.tags || [];
                const artworkUrl = resolveMediaUrl(song.artwork_url || null);
                const primaryTag = songTags[0] || null;
                const primaryTagBg = 'var(--main-content-card-background-color)';
                const primaryTagText = 'var(--main-content-text-color)';
                const previewUrl = song.preview_url || null;
                const hasPreview = Boolean(previewUrl);
                const markerLabel = getQueueMarker(songId);
                const isAlreadyPlayed = songQueueStatusMap.get(songId) === 3;
                // A request here is free; a band that takes none offers none
                // (ENG-665). Members add songs either way, and a switch is not
                // a new request.
                const canRequestSong = !isSongInQueue(songId) && (!isAlreadyPlayed || allowRepeatRequests)
                  && (Boolean(switchEntryId) || freeRequestsOpen);
                const enterDelay = Math.min(index * 18, 260);

                return (
                  <div
                    className={`public-song-card-wrapper vinyl-sleeve-trigger ${songListAnimationState === 'in' ? 'public-song-card-wrapper--enter' : ''}`}
                    style={{ '--song-enter-delay': `${enterDelay}ms` }}
                    key={songId}
                  >
                    <article
                      className="public-song-card"
                      data-testid="public-song-card"
                      data-song-id={songId}
                      onMouseEnter={() => playPreview(song)}
                      onMouseLeave={stopPreview}
                    >
                      {markerLabel && <div className="public-song-played-marker">{markerLabel}</div>}

                      <VinylSleeve
                        artworkUrl={artworkUrl}
                        fallbackLabel={getCoverLabel(song)}
                        alt={t('publicSongList.card.coverArtAlt', { title: song.title })}
                        className="public-song-card-stage"
                        coverClassName={`public-song-cover public-song-jacket ${artworkUrl ? 'has-image' : ''} ${hasPreview ? 'has-preview' : ''}`}
                      >
                        <div className="public-song-jacket-tape" />
                        <div className="public-song-year-stamp">{getSongYear(song)}</div>

                        {primaryTag && (
                          <div
                            className="public-song-sticker"
                            style={{
                              backgroundColor: primaryTagBg,
                              color: primaryTagText,
                            }}
                          >
                            {primaryTag.Word || primaryTag.word}
                          </div>
                        )}

                        <div className="public-song-hover-info">
                          <div className="public-song-overlay-title">{song.title}</div>
                          <div className="public-song-overlay-artist">{song.artist}</div>
                          {canRequestSong && (
                            <button
                              type="button"
                              className="public-song-request-btn"
                              data-testid="public-song-request"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (switchEntryId) switchTo(song);
                                else openRequestModal(song, artworkUrl);
                              }}
                              disabled={switching}
                            >{switchEntryId ? t('publicSongList.switch.choose') : t('publicSongList.card.request')}</button>
                          )}
                          {hasPreview && (
                            <div className="public-song-hover-preview-indicator">{t('publicSongList.card.previewHint')}</div>
                          )}
                        </div>
                      </VinylSleeve>

                      <div className="public-song-meta public-song-card-meta">
                        <p className="public-song-title">{song.title}</p>
                        <p className="public-song-artist">{song.artist}</p>
                        <div className="public-song-meta-row">
                          <span className="public-song-meta-note">
                            {song.original_album || ''}
                          </span>
                          <FavoriteButton
                            title={song.title}
                            favorited={favorites.isFavorite(songId)}
                            busy={favorites.isPending(songId)}
                            onToggle={() => handleFavorite(song)}
                            testId={`favorite-${songId}`}
                          />
                        </div>
                      </div>
                    </article>
                  </div>
                );
              })
            )}
            </div>
          </section>

          {isBandLive && (
            <aside className="public-songlist-queue-rail" aria-label={t('publicSongList.queue.railLabel')}>
                {hasDonationMethods && (
                  <section className="public-songlist-donation-panel" aria-label={t('publicSongList.support.panelLabel')}>
                    <header className="public-songlist-donation-header">
                      <span className="public-songlist-donation-kicker">{t('publicSongList.support.kicker')}</span>
                      <p className="public-songlist-donation-copy">
                        {t('publicSongList.support.copy')}
                      </p>
                    </header>

                    <div className="public-songlist-donation-methods">
                      {donationMethods.map((method) => (
                        <button
                          key={method.key}
                          type="button"
                          className="public-songlist-donation-method-chip"
                          onClick={() => openTipModal(null)}
                        >
                          {method.label}
                        </button>
                      ))}
                    </div>

                    <div className="public-songlist-donation-amounts" aria-label={t('publicSongList.support.amountsLabel')}>
                      <button type="button" className="public-songlist-donation-amount" onClick={() => openTipModal(5)}>{t('tip.amount', { amount: 5 })}</button>
                      <button type="button" className="public-songlist-donation-amount" onClick={() => openTipModal(10)}>{t('tip.amount', { amount: 10 })}</button>
                      <button type="button" className="public-songlist-donation-amount" onClick={() => openTipModal(20)}>{t('tip.amount', { amount: 20 })}</button>
                      <button type="button" className="public-songlist-donation-amount" onClick={() => openTipModal(50)}>{t('tip.amount', { amount: 50 })}</button>
                      <button type="button" className="public-songlist-donation-amount public-songlist-donation-amount--featured" onClick={() => openTipModal(100)}>{t('tip.amount', { amount: 100 })}</button>
                      <button type="button" className="public-songlist-donation-amount" onClick={() => openTipModal(null)}>{t('publicSongList.support.other')}</button>
                    </div>
                  </section>
                )}
                <div className="public-songlist-queue-header">
                  <h2>{t('publicSongList.queue.heading')}</h2>
                  <span>{queueSongs.length}</span>
                </div>
                <div className="public-songlist-queue-body">
                  {queueSongs.length === 0 ? (
                    <div className="public-songlist-queue-empty">{t('publicSongList.queue.empty')}</div>
                  ) : (
                    queueSongs.map((item, index) => (
                      <div className="public-songlist-queue-item" key={`${item.id || index}`}>
                        <div className="public-songlist-queue-position">{t('publicSongList.queue.position', { position: index + 1 })}</div>
                        <div className="public-songlist-queue-thumb">
                          {item.artworkUrl ? (
                            <img src={item.artworkUrl} alt="" />
                          ) : (
                            <span>{item.title?.slice(0, 2).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="public-songlist-queue-meta">
                          <div className="public-songlist-queue-title">{item.title}</div>
                          <div className="public-songlist-queue-artist">{item.artist}</div>
                          {item.requestedBy && (
                            <div className="public-songlist-queue-requested-by">{t('publicSongList.queue.requestedBy', { name: item.requestedBy })}</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
            </aside>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default PublicSongList;
